import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Menu, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ConfigurationFilters, FilterIcon, OtherFilters, SavedFilters } from "../../../assets/icons";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { getMenuSections } from "../../../context/FiltersProvider/selectors/menuSelectors";
import { useNavigationPreferences } from "../../../hooks/usePreferences";
import { FilterContexType } from "../../layout/ViewHeader";
import { FiltersAccordion } from "./components/FiltersAccordion";

type Props = {
	visibility?: FilterContexType;
};

export const SECTION_ICONS: Record<string, React.ReactNode> = {
	saved: <SavedFilters />,
	available: <OtherFilters />,
	configurations: <ConfigurationFilters />,
};

export const FiltersMenu = ({ visibility }: Props) => {
	const { t } = useTranslation("common");
	const { navigationPreferences } = useNavigationPreferences();

	const { registry, advancedMode } = useFiltersRegistry();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [search, setSearch] = useState("");
	const [expandedSections, setExpandedSections] = useState<string[]>([]);

	// Menu sections
	const sections = useMemo(() => {
		const safeVisibility = (visibility ?? []) as FilterContexType;

		const effectiveVisibility =
			navigationPreferences.transportsPerspective === "logical"
				? ([...safeVisibility, "entities"] as FilterContexType)
				: safeVisibility;

		return getMenuSections(registry, t, {
			visibility: effectiveVisibility,
			search,
		});
	}, [navigationPreferences.transportsPerspective, registry, t, visibility, search]);

	// Auto expand sections with results on search
	useEffect(() => {
		if (!search.trim()) {
			setExpandedSections([]);
			return;
		}

		const expanded = sections.filter((section) => section.items.length > 0).map((section) => section.id);

		setExpandedSections(expanded);
	}, [search]);

	// Open menu
	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	// Close menu
	const handleClose = () => {
		setAnchorEl(null);
		setSearch("");
		setExpandedSections([]);
	};

	// Toggle accordion
	const toggleSection = (sectionId: string) => {
		setExpandedSections((prev) => {
			if (prev.includes(sectionId)) {
				return prev.filter((id) => id !== sectionId);
			}

			return [...prev, sectionId];
		});
	};

	// Use effect to close the menu when advanced mode is enabled
	useEffect(() => {
		if (advancedMode) {
			setAnchorEl(null);
		}
	}, [advancedMode]);

	return (
		<>
			<Tooltip title={advancedMode ? t("advanced mode enabled text") : ""} placement="top">
				<span>
					<Button
						ref={buttonRef}
						disabled={advancedMode}
						onClick={handleOpen}
						color="primary"
						startIcon={<FilterIcon />}
					>
						{t("filters")}
					</Button>
				</span>
			</Tooltip>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<Box
					sx={{
						width: 320,
						maxHeight: 500,
						overflowY: "scroll",
						scrollbarGutter: "stable",
					}}
				>
					<TextField
						fullWidth
						size="small"
						placeholder={t("search filters")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{
							pt: 2,
							px: 2,
							pb: 1,
							backgroundColor: "background.paper",
							position: "sticky",
							top: 0,
							zIndex: 10,
						}}
					/>

					{sections.map((section) => (
						<FiltersAccordion
							key={section.id}
							title={section.title}
							items={section.items}
							icon={SECTION_ICONS[section.id]}
							expanded={expandedSections.includes(section.id)}
							onToggle={() => toggleSection(section.id)}
						/>
					))}
				</Box>
			</Menu>
		</>
	);
};
